import {css} from '@emotion/css';
import {useMemo} from 'react';

import {useWolfTheme} from '../../contexts';

import {PanelTheme} from './types';

const useStyle = (componentTheme: PanelTheme, open: boolean, collapsable: boolean) => {
    const theme = useWolfTheme();
    const transitionSpeed = '400ms';

    return useMemo(
        () => ({
            panel: css({
                backgroundColor: `${
                    componentTheme === 'primary'
                        ? theme.palette.primary.backgroundLight
                        : theme.palette.background.container
                } !important`,
                border: `1px solid ${theme.palette.stroke.default}`,
                borderRadius: theme.borderRadius,
                color: theme.palette.foreground.default,
            }),

            wrapper: css({
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }),

            panelHeader: css({
                padding: `${theme.orbitalThree} ${theme.orbitalFour}`,
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: `border-bottom ${transitionSpeed} ease`,
                borderBottom: open ? `1px solid ${theme.palette.stroke.default}` : '1px solid transparent',
            }),

            toggle: css({
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: `transform ${transitionSpeed} ease`,
            }),

            panelIconWrapper: css({
                display: 'flex',
                alignItems: 'center',
                gap: theme.orbitalThree,
            }),

            title: css({
                display: 'block',

                '& > *': {
                    display: 'inline',
                },
            }),

            expandButton: css({
                paddingLeft: theme.orbitalTwo,
            }),

            panelActions: css({
                display: 'flex',
                gap: theme.orbitalTwo,

                ...(collapsable && {
                    paddingRight: theme.orbitalThree,
                    borderRight: `1px solid ${theme.palette.stroke.default}`,
                }),
            }),

            iconWrapper: css({
                display: 'inline-flex',
                padding: `${theme.orbitalTwo} 0`,

                '& svg': {
                    width: 28,
                    height: 28,
                },
            }),

            children: css({
                // For a11y hide all content from a screenreader when collapsed.
                visibility: open ? 'visible' : 'hidden',
                display: 'grid',
                gridTemplateRows: open ? '1fr' : '0fr',
                gridTemplateColumns: 'repeat(auto-fit, minmax(10px, 1fr))',
                overflow: 'hidden',
                transition: `grid-template-rows ${transitionSpeed}`,
                flexGrow: 1,
            }),

            childrenInner: css({
                minHeight: 0,
            }),

            childrenPadding: css({
                padding: theme.orbitalFour,
                boxSizing: 'border-box',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }),

            footer: css({
                display: 'flex',
                justifyContent: 'end',
                gap: theme.orbitalFour,
            }),
        }),
        [
            collapsable,
            componentTheme,
            open,
            theme.borderRadius,
            theme.orbitalFour,
            theme.orbitalThree,
            theme.orbitalTwo,
            theme.palette.background.container,
            theme.palette.foreground.default,
            theme.palette.primary.backgroundLight,
            theme.palette.stroke.default,
        ]
    );
};

export default useStyle;
